import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocationStore } from '../stores/location-store';
import { showSuccessNotification, showErrorNotification } from '@/components/common/utils/notify';
import { useTheme } from '@/components/common/ThemeProvider';
import { LocationDTO } from '@/shared/api/model';
import { AxiosError } from 'axios';

// Import Orval generated API client
import {
    useGetRootLocations,
    useGetLocationsByParentId,
    useGetDistrictsByRegion1,
    useGetWardsByDistrict1,
    useGetStreetsByWard1,
    useValidateLocationSelection,
    useGetLocationHierarchy,
    getRootLocations,
    getLocationsByParentId,
    getDistrictsByRegion1,
    getWardsByDistrict1,
    getStreetsByWard1,
    validateLocationSelection,
    getLocationHierarchy
} from '@/shared/api/client/location-management';

const useLocation = () => {
    const queryClient = useQueryClient();
    const { dark } = useTheme();
    const {
        locations,
        selectedLocation,
        locationHierarchy,
        isLoading,
        error,
        zones,
        regions,
        districts,
        wards,
        streets,
        selectedZone,
        selectedRegion,
        selectedDistrict,
        selectedWard,
        selectedStreet,

        setLocations,
        setSelectedLocation,
        setLocationHierarchy,
        setIsLoading,
        setError,
        setZones,
        setRegions,
        setDistricts,
        setWards,
        setStreets,
        setSelectedZone,
        setSelectedRegion,
        setSelectedDistrict,
        setSelectedWard,
        setSelectedStreet,
        resetSelections,
        resetFromLevel
    } = useLocationStore();

    // Error handler
    const handleError = useCallback((message: string, error: AxiosError) => {
        console.error(`Error ${message}:`, error);
        setError(`Failed to load ${message}`);
        showErrorNotification('Error', `Failed to load ${message}`, dark);
    }, [dark, setError]);

    // Load zones (top level locations)
    const {
        data: zonesData,
        isLoading: isLoadingZones,
        refetch: refetchZones
    } = useGetRootLocations({
        query: {
            // @ts-ignore
            onError: (error: AxiosError) => {
                console.error('Error loading zones:', error);
                setError('Failed to load zones');
                showErrorNotification('Error', 'Failed to load zones', dark);
            }
        }
    });

    useEffect(() => {
        if (zonesData) {
            setZones(zonesData);
        }
    }, [zonesData, setZones]);

    // Load regions when zone is selected
    const {
        data: regionsData,
        isLoading: isLoadingRegions,
        refetch: refetchRegions
    } = useGetLocationsByParentId(
        selectedZone?.id || 0,
        {
            query: {
                enabled: !!selectedZone,
                // @ts-ignore
                onError: (error: AxiosError) => {
                    handleError('regions', error);
                }
            }
        }
    );

    useEffect(() => {
        if (regionsData && selectedZone) {
            setRegions(regionsData);
        }
    }, [regionsData, selectedZone, setRegions]);

    // Load districts when region is selected
    const {
        data: districtsData,
        isLoading: isLoadingDistricts,
        refetch: refetchDistricts
    } = useGetDistrictsByRegion1(
        selectedRegion?.id || 0,
        {
            query: {
                enabled: !!selectedRegion,
                // @ts-ignore
                onError: (error: AxiosError) => {
                    handleError('districts', error);
                }
            }
        }
    );

    useEffect(() => {
        if (districtsData && selectedRegion) {
            setDistricts(districtsData);
        }
    }, [districtsData, selectedRegion, setDistricts]);

    // Load wards when district is selected
    const {
        data: wardsData,
        isLoading: isLoadingWards,
        refetch: refetchWards
    } = useGetWardsByDistrict1(
        selectedDistrict?.id || 0,
        {
            query: {
                enabled: !!selectedDistrict,
                // @ts-ignore
                onError: (error: AxiosError) => {
                    handleError('wards', error);
                }
            }
        }
    );

    useEffect(() => {
        if (wardsData && selectedDistrict) {
            setWards(wardsData);
        }
    }, [wardsData, selectedDistrict, setWards]);

    // Load streets when ward is selected
    const {
        data: streetsData,
        isLoading: isLoadingStreets,
        refetch: refetchStreets
    } = useGetStreetsByWard1(
        selectedWard?.id || 0,
        {
            query: {
                enabled: !!selectedWard,
                // @ts-ignore
                onError: (error: AxiosError) => {
                    handleError('streets', error);
                }
            }
        }
    );

    useEffect(() => {
        if (streetsData && selectedWard) {
            setStreets(streetsData);
        }
    }, [streetsData, selectedWard, setStreets]);

    // Handle level changes
    const handleZoneChange = useCallback((zone: LocationDTO | null | undefined) => {
        setSelectedZone(zone);
        resetFromLevel('zone');
    }, [setSelectedZone, resetFromLevel]);

    const handleRegionChange = useCallback((region: LocationDTO | null | undefined) => {
        setSelectedRegion(region);
        resetFromLevel('region');
    }, [setSelectedRegion, resetFromLevel]);

    const handleDistrictChange = useCallback((district: LocationDTO | null | undefined) => {
        setSelectedDistrict(district);
        resetFromLevel('district');
    }, [setSelectedDistrict, resetFromLevel]);

    const handleWardChange = useCallback((ward: LocationDTO | null | undefined) => {
        setSelectedWard(ward);
        resetFromLevel('ward');
    }, [setSelectedWard, resetFromLevel]);

    const handleStreetChange = useCallback((street: LocationDTO | null | undefined) => {
        setSelectedStreet(street);
    }, [setSelectedStreet]);

    // Load location hierarchy
    const loadLocationHierarchy = useCallback(async (locationId: number) => {
        setIsLoading(true);
        try {
            const response = await getLocationHierarchy(locationId);
            const hierarchy = response;

            if (hierarchy) {
                setLocationHierarchy(hierarchy);

                // Update selected items in cascade
                if (hierarchy.zone) setSelectedZone(hierarchy.zone);
                if (hierarchy.region) setSelectedRegion(hierarchy.region);
                if (hierarchy.district) setSelectedDistrict(hierarchy.district);
                if (hierarchy.ward) setSelectedWard(hierarchy.ward);
                if (hierarchy.street) setSelectedStreet(hierarchy.street);
            }
        } catch (err) {
            console.error('Error loading location hierarchy:', err);
            setError('Failed to load location hierarchy');
            showErrorNotification('Error', 'Failed to load location hierarchy', dark);
        } finally {
            setIsLoading(false);
        }
    }, [
        setIsLoading,
        setLocationHierarchy,
        setSelectedZone,
        setSelectedRegion,
        setSelectedDistrict,
        setSelectedWard,
        setSelectedStreet,
        setError,
        dark
    ]);

    // Validate location selection based on a configuration
    const validateLocations = useCallback(async (params: {
        configName: string;
        zoneId?: number;
        regionId?: number;
        districtId?: number;
        wardId?: number;
        streetId?: number;
        latitude?: number;
        longitude?: number;
        gpsAccuracy?: number;
        postalCode?: string;
    }) => {
        try {
            // Extract configName and create validation request object
            const { configName, ...locationData } = params;

            const response = await validateLocationSelection(
                locationData,
                { configName }
            );
            return response;
        } catch (err) {
            console.error('Error validating location selection:', err);
            showErrorNotification('Error', 'Failed to validate location selection', dark);
            return { valid: false, errors: ['Validation failed due to an error'] };
        }
    }, [dark]);

    // Track overall loading state
    useEffect(() => {
        const currentlyLoading = isLoadingZones || isLoadingRegions ||
            isLoadingDistricts || isLoadingWards || isLoadingStreets;
        setIsLoading(currentlyLoading);
    }, [
        isLoadingZones,
        isLoadingRegions,
        isLoadingDistricts,
        isLoadingWards,
        isLoadingStreets,
        setIsLoading
    ]);

    return {
        locations,
        selectedLocation,
        locationHierarchy,
        isLoading,
        error,
        zones,
        regions,
        districts,
        wards,
        streets,
        selectedZone,
        selectedRegion,
        selectedDistrict,
        selectedWard,
        selectedStreet,
        handleZoneChange,
        handleRegionChange,
        handleDistrictChange,
        handleWardChange,
        handleStreetChange,
        loadLocationHierarchy,
        validateLocationSelection: validateLocations,
        refetchZones,
        refetchRegions,
        refetchDistricts,
        refetchWards,
        refetchStreets,
        resetSelections
    };
};

export default useLocation;